<template>
	<section class="filter-tag-list">
		<div v-if="items.length">
			<div
				v-if="!isModal && showHeader"
				class="filter-tag-list-header"
			>
				<span
					v-if="showHeaderTitle || showResetInHeader"
					class="filter-tag-list-header__title"
				>
					{{ translate('filterTagList.selectedFilter') }}
				</span>
				<LinkButton
					v-if="showResetInHeader"
					:label="translate('delete')"
					class="filter-tag-list-header__reset-all"
					:with-icon="false"
					@click="resetAll"
				/>
			</div>
			<FilterHeader
				v-if="isModal"
				:title="translate('filterTagList.selectedFilter')"
				:delete-button-string="translate('delete')"
				:is-checked="true"
				class="filter-tag-list-header"
				@delete="resetAll"
			/>

			<div class="filter-tag-list__list">
				<LinkButton
					v-if="!showResetInHeader"
					:label="translate('filterTagList.deleteAll')"
					class="filter-tag-list__reset-all"
					:with-icon="false"
					@click="resetAll"
				/>
				<ChipBase
					v-for="item in visibleItems"
					:key="item.type + item.label"
					class="filter-tag-list__item"
					:label="item.label"
					:thin="true"
					:with-delete="true"
					@delete-click="resetFilter(item)"
					@click="resetFilter(item)"
					@vue:mounted="onTagMounted(item.label, item.type)"
				/>
				<Transition
					v-for="item in hiddenItems"
					:key="item.type + item.label"
				>
					<ChipBase
						v-if="isCollapseOpen"
						class="filter-tag-list__item"
						:label="item.label"
						:thin="true"
						:with-delete="true"
						@delete-click="resetFilter(item)"
						@click="resetFilter(item)"
						@vue:mounted="onTagMounted(item.label, item.type)"
					/>
				</Transition>
			</div>

			<ShowMoreToggle
				v-if="shouldCollapse"
				:label-more="translate('filterTagList.more')"
				:label-less="translate('filterTagList.less')"
				@ShowMoreToggle:onToggle="handleToggle"
			/>
		</div>
	</section>
</template>

<script lang="ts" setup>
import type { SearchFormDataType } from '@interfaces/search-form';
import { computed, ref, onMounted } from 'vue';
import { EventBus } from '@global-js/event-bus';
import { useTranslations } from '@helper/composables/translations';

import { useStore } from '@/components/common/store';
import type { AdditionalCheckboxFilters, MostPopularFilters } from '@/interfaces/checkbox-filter-types';
import FilterAnalytics from '@/js/modules/analytics/FilterAnalytics';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import ChipBase from '@lmt-rpb/Atoms/ChipBase/ChipBase.vue';
import LinkButton from '@lmt-rpb/Atoms/LinkButton/LinkButton.vue';
import ShowMoreToggle from '@lmt-rpb/Atoms/ShowMoreToggle/ShowMoreToggle.vue';
import FilterHeader from '@lmt-rpb/FilterHeader/FilterHeader.vue';
import { useFilters } from 'src/composables/useFIlters';

interface Props {
	emitAnalytics?: boolean;
	showResetInHeader?: boolean;
	showHeaderTitle?: boolean;
	isModal?: boolean;
}

const { emitAnalytics = true, showResetInHeader = false, showHeaderTitle = false, isModal = false } = defineProps<Props>();

const isMounted = ref(false);
const store = useStore();

const showHeader = computed(() => showResetInHeader || showHeaderTitle);

const { translate } = useTranslations();

const { isMobile } = storeToRefs(useBreakpointStore());
const formData = computed((): SearchFormDataType => store.state.searchMask as SearchFormDataType);

const isCollapseOpen = ref(false);

const handleToggle = (b: boolean): void => {
	isCollapseOpen.value = b;
};

const { activeFilters: items } = useFilters();

const maxItems = 8;

const shouldCollapse = computed(() => {
	return isMobile.value && items.value.length > maxItems && !showResetInHeader;
});

const visibleItems = computed(() => {
	if (shouldCollapse.value) {
		return items.value.slice(0, maxItems);
	}
	return items.value;
});

const hiddenItems = computed(() => {
	if (shouldCollapse.value) {
		return items.value.slice(maxItems);
	}
	return [];
});
const locationIdName = computed((): 'RegionID' | 'CityID' =>
	store.getters.locationType === 'COUNTRY' || store.getters.locationType === 'REGIONGROUP' ? 'RegionID' : 'CityID'
);
const onTagMounted = (label: string, type?: string) => {
	if (!isMounted.value || !emitAnalytics) return;
	if (type === 'destinationTypes') {
		label = locationIdName.value === 'RegionID' ? 'Region' : 'Ort';
	}
	FilterAnalytics.createInstance(label).fireFilterSelected();
};

const resetAll = (): void => {
	store.dispatch('resetFormData');
	if (!isModal) {
		EventBus.$emit('SearchMask:MobileSubmit');
	}
};

const resetFilter = (item: { label: string; value: string | boolean; type: string; attrType?: string }): void => {
	type FormDataKey = keyof typeof formData.value;
	if (['boardTypes', 'roomTypes', 'roomViews', 'transferTypes'].indexOf(item.type) !== -1) {
		// update stored value without this item
		const stored = formData.value[item.type as FormDataKey] as string[];

		const updated = item.type === 'roomViews' ? [] : stored.filter((storedItem: string) => storedItem !== item.value);
		store.dispatch('setFormData', {
			...formData.value,
			[item.type]: updated,
		});
	} else if (['mostPopularFilters'].indexOf(item.type) !== -1 && item.attrType) {
		const stored = formData.value[item.type as FormDataKey] as MostPopularFilters;
		type StoredDataKey = keyof typeof stored;
		const storedFilter = stored ? stored[item.attrType as StoredDataKey] : [];
		const updated = storedFilter?.filter((storedItem: string) => storedItem !== item.value);
		const storedMostPopular = formData.value.mostPopularFilters;
		store.dispatch('setFormData', {
			...formData.value,
			[item.type]: {
				...storedMostPopular,
				[item.attrType]: updated,
			},
		});
	} else if (['additionalCheckboxFilters'].indexOf(item.type) !== -1 && item.attrType) {
		const stored = formData.value[item.type as FormDataKey] as AdditionalCheckboxFilters;
		type StoredDataKey = keyof typeof stored;
		const storedFilter = stored ? stored[item.attrType as StoredDataKey] : [];
		const updated = storedFilter?.filter((storedItem: string) => storedItem !== item.value);
		const storedAdditionalCheckboxFilters = formData.value.additionalCheckboxFilters;
		store.dispatch('setFormData', {
			...formData.value,
			[item.type]: {
				...storedAdditionalCheckboxFilters,
				[item.attrType]: updated,
			},
		});
	} else {
		// reset stored value by init state
		store.dispatch('resetItem', item.type);

		// if type is destinationTypes we also need to reset the saved name
		if (['destinationTypes'].indexOf(item.type) !== -1) {
			store.dispatch('resetItem', 'destinationTypeName');
		} else if (['travelDuration'].indexOf(item.type) !== -1) {
			store.dispatch('resetItem', 'isTravelDurationExactSelected');
		}
	}

	if (!isModal) {
		// update filters
		EventBus.$emit('SearchMask:MobileSubmit');
	}
};
onMounted(() => {
	isMounted.value = true;
});
</script>

<style lang="scss" scoped>
.v-enter-active,
.v-leave-active {
	transition: opacity 0s;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
}

:deep() {
	.toggle {
		color: $chip-base-delete-icon;
	}

	.chip-base {
		color: $chip-base-delete-icon;
		border-color: $chip-base-delete-icon;
		max-width: 100%;

		&__label {
			text-overflow: ellipsis;
			white-space: nowrap;
			overflow: hidden;
		}
	}

	path {
		fill: $chip-base-delete-icon;
	}
}

@mixin reset {
	&__reset-all {
		color: $chip-base-delete-icon;
		display: inline-flex;
		font-size: $font-small-2;
		align-items: center;
		@content;
	}
}

.filter-tag-list {
	&-header {
		display: flex;
		justify-content: space-between;
		margin-bottom: 2.4rem;

		&__title {
			font-weight: $font-weight-semibold;
			font-size: $font-medium-3;
			color: $color-black;

			@media (min-width: $breakpoint-extralarge) {
				font-size: $font-medium-2;
			}
		}

		@include reset {
			:deep(.link-button__text) {
				text-decoration: none;
				font-size: $font-small-2;

				@media (min-width: $breakpoint-small) {
					font-size: $font-small-1;
				}
			}
		}
	}

	&__list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.8rem;
		list-style: none;
		align-items: center;
		margin-left: 0;
	}

	margin-bottom: 2.4rem;
	align-items: center;
	display: block;
	flex-wrap: wrap;
	justify-content: space-between;
	width: 100%;

	@include reset;
}
</style>
